import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, Paperclip, Phone, Headset, Scale, Handshake, Gavel, FileText, ShieldCheck, CheckCircle, ArrowRight, Briefcase, Home, Users, CreditCard, HeartHandshake, Building2, ShoppingBag, HelpCircle, Upload, AlertCircle, ChevronRight, Eye, Sliders, RotateCcw, MapPin } from 'lucide-react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
interface DashboardContext {
  toggleMobileSidebar: () => void;
}
type Answers = {
  matter?: string;
  subTheme?: string;
  matterNote?: string;
  amount?: string;
  litigation?: string;
  outcome?: string;
};
const SUB_THEMES: Record<string, string[]> = {
  Litigation: ['Civil lawsuit', 'Personal injury', 'Defamation', 'Breach of contract', 'Professional negligence', 'Other'],
  'Residential Landlord & Tenant': ['Eviction', 'Rent increase', 'Repairs & maintenance', 'Lease termination', 'Deposit dispute', 'Other'],
  'Real Estate Transaction': ['Purchase agreement', 'Sale of property', 'Mortgage issue', 'Title or deed problem', 'Closing dispute', 'Other'],
  'Real Estate Litigation': ['Boundary dispute', 'Construction defect', 'Condo or strata dispute', 'Foreclosure', 'Easement issue', 'Other'],
  'Contract Dispute': ['Breach of contract', 'Non-payment', 'Service not delivered', 'Contract interpretation', 'Cancellation dispute', 'Other'],
  'Debt / Collections': ['Owed money to me', 'Collection agency contact', 'Wage garnishment', 'Bankruptcy question', 'Debt validation', 'Other'],
  Family: ['Divorce or separation', 'Child custody', 'Child support', 'Spousal support', 'Adoption', 'Other'],
  'Consumer Issue': ['Defective product', 'Refund dispute', 'Misleading advertising', 'Warranty claim', 'Service complaint', 'Other']
};
const MATTERS = [{
  label: 'Litigation',
  icon: Gavel
}, {
  label: 'Residential Landlord & Tenant',
  icon: Home
}, {
  label: 'Real Estate Transaction',
  icon: Building2
}, {
  label: 'Real Estate Litigation',
  icon: Scale
}, {
  label: 'Contract Dispute',
  icon: FileText
}, {
  label: 'Debt / Collections',
  icon: CreditCard
}, {
  label: 'Family',
  icon: HeartHandshake
}, {
  label: 'Consumer Issue',
  icon: ShoppingBag
}];
const AMOUNTS = ['Under $1,000', '$1,000 - $5,000', '$5,000 - $15,000', '$15,000 - $35,000', 'Over $35,000', 'Not sure', 'Not applicable'];
const LITIGATION = ['No, nothing has started yet', 'I received a legal notice', 'A claim has already been filed', 'I have a court date', 'I am not sure'];
const OUTCOMES = ['Understand my rights', 'Prepare a document', 'Speak to a lawyer', 'Resolve through mediation', 'Start or respond to a small claims matter', 'Hire a lawyer to represent me'];
const QUESTIONS = ['What is your legal matter about?', 'What is the approximate amount involved in the dispute?', 'Is there already litigation or a formal legal process involved?', 'What outcome are you hoping for?'];
const FLOW_STEPS = ['Start Triage', 'Answer Questions', 'Review Recommendation', 'Choose Support Path', 'Continue'];
// Recommendation services
const SERVICES = {
  phone: {
    key: 'phone',
    icon: Phone,
    title: 'Telephone Legal Assistance',
    description: 'Speak with a legal professional by phone for guidance on your matter.',
    cta: 'Request a Call',
    pricing: 'included'
  },
  ai: {
    key: 'ai',
    icon: Sparkles,
    title: 'Use LawVo AI to get legal information',
    description: 'Ask LawVo AI questions and get plain-language legal information based on your issue.',
    cta: 'Continue with LawVo AI',
    pricing: 'included'
  },
  smallClaims: {
    key: 'smallClaims',
    icon: Gavel,
    title: 'Small Claims Court',
    description: 'Get guided support for preparing or responding to a small claims matter.',
    cta: 'Start Small Claims Flow',
    pricing: 'addon'
  },
  mediation: {
    key: 'mediation',
    icon: Handshake,
    title: 'Mediation',
    description: 'Explore a resolution path before going to court.',
    cta: 'Start Mediation Setup',
    pricing: 'addon'
  },
  representation: {
    key: 'representation',
    icon: Scale,
    title: 'Legal Representation',
    description: 'Connect with a lawyer who may be able to represent you.',
    cta: 'Find Legal Representation',
    pricing: 'addon'
  }
} as const;
type ServiceKey = keyof typeof SERVICES;
function rankRecommendations(a: Answers): {
  primary: ServiceKey;
  also: ServiceKey[];
  available: ServiceKey[];
  explanation: string;
} | null {
  if (!a.matter || !a.amount || !a.litigation || !a.outcome) {
    return null;
  }
  // Signals
  const wantsRights = a.outcome === 'Understand my rights';
  const wantsDocument = a.outcome === 'Prepare a document';
  const wantsLawyerTalk = a.outcome === 'Speak to a lawyer';
  const wantsRep = a.outcome === 'Hire a lawyer to represent me';
  const wantsMediation = a.outcome === 'Resolve through mediation';
  const wantsSmallClaims = a.outcome === 'Start or respond to a small claims matter';
  const noLitigation = a.litigation === 'No, nothing has started yet';
  const noticeReceived = a.litigation === 'I received a legal notice';
  const claimFiled = a.litigation === 'A claim has already been filed';
  const hasCourtDate = a.litigation === 'I have a court date';
  const inFormalProcess = claimFiled || hasCourtDate;
  const smallClaimsRange = a.amount === 'Under $1,000' || a.amount === '$1,000 - $5,000' || a.amount === '$5,000 - $15,000' || a.amount === '$15,000 - $35,000';
  const highValue = a.amount === 'Over $35,000';
  const disputeMatter = a.matter === 'Contract Dispute' || a.matter === 'Debt / Collections' || a.matter === 'Consumer Issue' || a.matter === 'Landlord & Tenant';
  // Initialize scores
  const scores: Record<ServiceKey, number> = {
    phone: 10,
    ai: 5,
    smallClaims: 0,
    mediation: 0,
    representation: 0
  };
  // --- Outcome-driven scoring (strongest signal) ---
  if (wantsRights) {
    scores.ai += 80;
    scores.phone += 20;
    scores.representation -= 30;
    scores.smallClaims -= 20;
    scores.mediation -= 10;
  }
  if (wantsDocument) {
    scores.ai += 60;
    scores.phone += 20;
  }
  if (wantsLawyerTalk) {
    scores.phone += 80;
    scores.ai -= 10;
  }
  if (wantsRep) {
    scores.representation += 100;
    scores.phone += 25;
    scores.ai -= 50;
    scores.smallClaims -= 20;
    scores.mediation -= 20;
  }
  if (wantsMediation) {
    scores.mediation += 90;
    scores.phone += 15;
    scores.smallClaims -= 30;
    scores.representation -= 20;
  }
  if (wantsSmallClaims) {
    scores.smallClaims += 90;
    scores.phone += 15;
    scores.mediation -= 30;
    scores.ai -= 10;
  }
  // --- Litigation / formal process ---
  if (noLitigation) {
    scores.mediation += 25;
    scores.smallClaims += 5;
  }
  if (noticeReceived) {
    scores.phone += 50;
    scores.representation += 25;
    scores.ai -= 20;
    scores.mediation -= 10;
  }
  if (claimFiled) {
    scores.phone += 50;
    scores.representation += 40;
    scores.smallClaims += 30;
    scores.ai -= 30;
    scores.mediation -= 30;
  }
  if (hasCourtDate) {
    scores.phone += 60;
    scores.representation += 50;
    scores.ai -= 40;
    scores.mediation -= 40;
  }
  // --- Amount ---
  if (smallClaimsRange) {
    scores.smallClaims += 15;
  }
  if (highValue) {
    scores.smallClaims -= 50; // out of small claims range
    scores.representation += 30;
    scores.phone += 10;
  }
  // --- Matter type nudges ---
  if (disputeMatter && smallClaimsRange && !wantsRights) {
    scores.smallClaims += 10;
    scores.mediation += 10;
  }
  if (a.matter === 'Family' || a.matter === 'Real Estate Transaction' || a.matter === 'Real Estate Litigation' || a.matter === 'Litigation') {
    scores.representation += 15;
    scores.phone += 10;
  }
  // --- Hard constraints ---
  if (highValue) {
    scores.smallClaims = Math.min(scores.smallClaims, 5);
  }
  if (inFormalProcess) {
    scores.ai = Math.min(scores.ai, 10);
    scores.mediation = Math.min(scores.mediation, 15);
  }
  // Rank
  const ordered = (Object.entries(scores) as [ServiceKey, number][]).sort((a, b) => b[1] - a[1]);
  const [primary] = ordered[0];
  // Only include in "also" if reasonably strong AND meaningfully lower than primary
  const ALSO_MIN = 35;
  const also: ServiceKey[] = ordered.slice(1).filter(([_, s]) => s >= ALSO_MIN).slice(0, 2).map(([k]) => k);
  // Always surface LawVo AI as a backup option when it isn't the primary pick
  if (primary !== 'ai' && !also.includes('ai')) {
    if (also.length >= 2) {
      also[also.length - 1] = 'ai';
    } else {
      also.push('ai');
    }
  }
  const used = new Set<ServiceKey>([primary, ...also]);
  const available = ordered.map(([k]) => k).filter(k => !used.has(k));
  // Explanation
  let explanation = '';
  if (primary === 'representation') {
    explanation = 'Your situation suggests you may need a lawyer to act on your behalf - either because of formal legal action, a high-value matter, or your stated preference.';
  } else if (primary === 'phone') {
    explanation = inFormalProcess ? 'Because a formal legal process is underway, speaking with a legal professional by phone is the safest first step.' : 'A short call with a legal professional is the most direct way to get clarity on your options.';
  } else if (primary === 'ai') {
    explanation = 'You mainly want information and nothing formal has started - LawVo AI can give you plain-language answers right away.';
  } else if (primary === 'mediation') {
    explanation = 'No formal litigation has started and your goal is a resolution, so mediation is a practical first step before considering court.';
  } else if (primary === 'smallClaims') {
    explanation = 'The dispute amount fits the small claims range and your goal aligns with a small claims process, so this path is a strong fit.';
  }
  return {
    primary,
    also,
    available,
    explanation
  };
}
export function LegalSupportTriage() {
  const navigate = useNavigate();
  const ctx = useOutletContext<DashboardContext>();
  const toggleMobileSidebar = ctx?.toggleMobileSidebar ?? (() => {});
  const [presentationMode, setPresentationMode] = useState(false);
  const [step, setStep] = useState(0); // 0..6 (6 = complete)
  const [answers, setAnswers] = useState<Answers>({});
  const [matterNote, setMatterNote] = useState('');
  const [selectedPath, setSelectedPath] = useState<ServiceKey | null>(null);
  const [typing, setTyping] = useState(false);
  const [introReady, setIntroReady] = useState(false);
  // Call booking sub-flow state
  const [callBookingStep, setCallBookingStep] = useState<'idle' | 'pickWindows' | 'enterPhone' | 'confirmed'>('idle');
  const [selectedWindows, setSelectedWindows] = useState<string[]>([]);
  const [callPhone, setCallPhone] = useState('');
  const [callNote, setCallNote] = useState('');
  const [callTyping, setCallTyping] = useState(false);
  const callWindows = useMemo(() => getCallWindows(8), [callBookingStep]);
  // Representation booking sub-flow state
  const [representationStep, setRepresentationStep] = useState<'idle' | 'pickWindows' | 'enterPostalCode' | 'confirmed'>('idle');
  const [representationWindows, setRepresentationWindows] = useState<string[]>([]);
  const [postalCode, setPostalCode] = useState('');
  const [repTyping, setRepTyping] = useState(false);
  const repWindows = useMemo(() => getCallWindows(48), [representationStep]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const setAnswer = (key: keyof Answers, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
    if (step < 4) setStep(s => s + 1);
  };
  const loadDemo = () => {
    setAnswers({
      matter: 'Contract Dispute',
      subTheme: 'Service not delivered',
      matterNote: 'A vendor stopped delivering services after I paid a deposit and is refusing a refund.',
      amount: '$5,000 - $15,000',
      litigation: 'No, nothing has started yet',
      outcome: 'Resolve through mediation'
    });
    setMatterNote('A vendor stopped delivering services after I paid a deposit and is refusing a refund.');
    setStep(4);
  };
  const resetTriage = () => {
    setAnswers({});
    setMatterNote('');
    setStep(0);
    setSelectedPath(null);
    setIntroReady(false);
    setTyping(false);
    setCallBookingStep('idle');
    setSelectedWindows([]);
    setCallPhone('');
    setCallNote('');
    setCallTyping(false);
    setRepresentationStep('idle');
    setRepresentationWindows([]);
    setPostalCode('');
    setRepTyping(false);
  };
  // Recommendation CTA handler - phone & representation paths trigger sub-flows
  const handleRecommendationSelect = (k: ServiceKey) => {
    setSelectedPath(k);
    if (k === 'phone' && callBookingStep === 'idle') {
      setCallTyping(true);
      setTimeout(() => {
        setCallTyping(false);
        setCallBookingStep('pickWindows');
      }, 700);
    }
    if (k === 'representation' && representationStep === 'idle') {
      setRepTyping(true);
      setTimeout(() => {
        setRepTyping(false);
        setRepresentationStep('pickWindows');
      }, 700);
    }
  };
  const toggleWindow = (label: string) => {
    setSelectedWindows(prev => {
      if (prev.includes(label)) return prev.filter(w => w !== label);
      if (prev.length >= 2) return prev;
      return [...prev, label];
    });
  };
  const confirmWindows = () => {
    if (selectedWindows.length !== 2) return;
    setCallTyping(true);
    setTimeout(() => {
      setCallTyping(false);
      setCallBookingStep('enterPhone');
    }, 800);
  };
  const confirmBooking = () => {
    const digits = callPhone.replace(/\D/g, '');
    if (digits.length < 10) return;
    setCallTyping(true);
    setTimeout(() => {
      setCallTyping(false);
      setCallBookingStep('confirmed');
    }, 1000);
  };
  const toggleRepWindow = (label: string) => {
    setRepresentationWindows(prev => {
      if (prev.includes(label)) return prev.filter(w => w !== label);
      if (prev.length >= 2) return prev;
      return [...prev, label];
    });
  };
  const confirmRepWindows = () => {
    if (representationWindows.length !== 2) return;
    setRepTyping(true);
    setTimeout(() => {
      setRepTyping(false);
      setRepresentationStep('enterPostalCode');
    }, 800);
  };
  const confirmRepresentation = () => {
    if (!isValidPostalCode(postalCode)) return;
    setRepTyping(true);
    setTimeout(() => {
      setRepTyping(false);
      setRepresentationStep('confirmed');
    }, 1000);
  };
  const recommendation = useMemo(() => rankRecommendations(answers), [answers]);
  // Flow indicator active step
  const flowActiveIndex = step === 0 ? 0 : step < 4 ? 1 : selectedPath ? 3 : 2;
  // Show typing indicator briefly when step advances (a new AI question is about to appear)
  useEffect(() => {
    if (step === 0) return;
    setTyping(true);
    const t = setTimeout(() => setTyping(false), 650);
    return () => clearTimeout(t);
  }, [step]);
  // Intro reveal: after the greeting, show a typing indicator then reveal Q1 around 2s in
  useEffect(() => {
    if (step !== 0) return;
    setIntroReady(false);
    setTyping(false);
    const showTyping = setTimeout(() => setTyping(true), 1200);
    const showQ1 = setTimeout(() => {
      setTyping(false);
      setIntroReady(true);
    }, 2000);
    return () => {
      clearTimeout(showTyping);
      clearTimeout(showQ1);
    };
  }, [step]);
  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  }, [step, typing, answers, callBookingStep, callTyping, selectedWindows.length, representationStep, repTyping, representationWindows.length]);
  // Helper to know which is the most recent active question index for avatar pulse
  const activeIndex = step; // 0..6
  return <div className="h-screen bg-white flex flex-col overflow-hidden" data-id="element-3035">
      <DashboardHeader categories={[]} onMenuClick={toggleMobileSidebar} onNavigate={path => navigate(path)} data-id="element-3036" />

      <main className="flex-1 min-h-0 w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6 flex flex-col gap-4" data-id="element-3037">
        {/* Unified Chat */}
        <section className="flex-1 min-h-0 bg-white border border-borderColor rounded-2xl shadow-sm flex flex-col overflow-hidden" data-id="element-3038">
          {/* Chat header */}
          <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-borderColor" data-id="element-3039">
            <div className="flex items-center gap-3 min-w-0" data-id="element-3040">
              <div className="w-10 h-10 rounded-full bg-aliceBlue flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-3041">
                <img src="https://cdn.magicpatterns.com/uploads/frDxN6VkzqMSZkD4nJP5uk/Screenshot_2025-12-31_at_11.06.59_AM.png" alt="LawVo AI" className="w-9 h-9 object-contain" data-id="element-3042" />
              </div>
              <div className="min-w-0" data-id="element-3043">
                <h2 className="text-base font-semibold text-primary leading-tight truncate" data-id="element-3044">
                  LawVo AI Triage
                </h2>
                <p className="text-xs text-secondary mt-0.5 truncate" data-id="element-3045">
                  Guided legal support questions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3046">
              <div className="hidden sm:flex items-center gap-2 pr-3 border-r border-borderColor" data-id="element-3047">
                <span className="text-[10px] uppercase tracking-wider text-secondary font-medium" data-id="element-3048">
                  Exclusively for
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/3xNLTyr3gfbDGuLh4Kjyph/telus-digital-spotlight-2025-11-27-075127787.png" alt="TELUS Digital" className="h-5 lg:h-6 w-auto object-contain" data-id="element-3049" />
              </div>
              <ProgressDots step={step} total={4} data-id="element-3050" />
              {step > 0 && <>
                  <span className="hidden sm:block h-5 w-px bg-borderColor" data-id="element-3051" />
                  <button type="button" onClick={resetTriage} className="inline-flex items-center justify-center text-secondary hover:text-violetBlue border border-borderColor hover:border-violetBlue rounded-lg p-1.5 transition-colors" aria-label="Restart chat" title="Restart" data-id="element-3052">
                    <RotateCcw className="w-3.5 h-3.5" data-id="element-3053" />
                  </button>
                </>}
            </div>
          </div>

          {/* Messages - ENHANCED ANIMATED VERSION */}
          <div ref={scrollRef} className="flex-1 min-h-0 px-5 py-5 space-y-5 bg-white overflow-y-auto scroll-smooth" data-id="element-3054">
            <AnimatePresence initial={false} data-id="element-3055">
              <motion.div key="intro" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-3056">
                <AIMessage pulse={activeIndex === 0 && !answers.matter} data-id="element-3057">
                  Hi, I'm here to help understand your legal situation and guide
                  you to the right support option. I'll ask a few quick
                  questions first.
                </AIMessage>
              </motion.div>

              {/* Typing indicator before Q1 (intro reveal) */}
              {step === 0 && typing && !introReady && <motion.div key="typing-intro" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3058">
                  <Typing data-id="element-3059" />
                </motion.div>}

              {/* Q1 */}
              {(introReady || step > 0) && <motion.div key="q1" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              ease: 'easeOut',
              delay: 0.1
            }} data-id="element-3060">
                  <AIMessage pulse={activeIndex === 0 && !answers.matter} data-id="element-3061">
                    <p className="font-medium text-primary mb-3" data-id="element-3062">
                      {QUESTIONS[0]}
                    </p>
                    {!answers.matter ? <div className="space-y-3" data-id="element-3063">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-3064">
                          {MATTERS.map((m, i) => {
                      const Icon = m.icon;
                      return <motion.button key={m.label} type="button" onClick={() => setAnswers(p => ({
                        ...p,
                        matter: m.label
                      }))} initial={{
                        opacity: 0,
                        y: 6
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        duration: 0.25,
                        delay: 0.04 * i
                      }} whileHover={{
                        y: -2
                      }} whileTap={{
                        scale: 0.96
                      }} className="flex items-center gap-2 px-3 py-2 bg-white border border-borderColor hover:border-violetBlue hover:bg-aliceBlue hover:shadow-sm text-primary text-sm rounded-lg transition-colors text-left" data-id="element-3065">
                                <Icon className="w-4 h-4 text-violetBlue flex-shrink-0" data-id="element-3066" />
                                <span className="leading-tight" data-id="element-3067">{m.label}</span>
                              </motion.button>;
                    })}
                        </div>
                        <textarea value={matterNote} onChange={e => setMatterNote(e.target.value)} onBlur={() => setAnswers(p => ({
                    ...p,
                    matterNote
                  }))} rows={2} placeholder="Briefly describe your situation..." className="w-full bg-white border border-borderColor rounded-lg px-3 py-2 text-sm text-primary placeholder-disabled focus:outline-none focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 resize-none transition-all" data-id="element-3068" />
                      </div> : <div className="space-y-3" data-id="element-3069">
                        <AnimatedUserChip text={answers.matter} data-id="element-3070" />
                        {!answers.subTheme && SUB_THEMES[answers.matter] && <motion.div initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.15
                  }} className="pt-1" data-id="element-3071">
                            <p className="text-sm text-primary mb-2 font-medium" data-id="element-3072">
                              Which best describes your{' '}
                              <span className="text-violetBlue" data-id="element-3073">
                                {answers.matter.toLowerCase()}
                              </span>{' '}
                              issue?
                            </p>
                            <ChipGroup options={SUB_THEMES[answers.matter]} onSelect={v => setAnswer('subTheme', v)} data-id="element-3074" />
                          </motion.div>}
                        {answers.subTheme && <AnimatedUserChip text={answers.subTheme} data-id="element-3075" />}
                      </div>}
                  </AIMessage>
                </motion.div>}

              {/* Typing indicator before Q2 */}
              {step === 1 && typing && <motion.div key="typing-1" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3076">
                  <Typing data-id="element-3077" />
                </motion.div>}

              {/* Q2 */}
              {step >= 1 && !typing && <motion.div key="q2" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-3078">
                  <AIMessage pulse={activeIndex === 1 && !answers.amount} data-id="element-3079">
                    <p className="font-medium text-primary mb-3" data-id="element-3080">
                      {QUESTIONS[1]}
                    </p>
                    {!answers.amount ? <ChipGroup options={AMOUNTS} onSelect={v => setAnswer('amount', v)} data-id="element-3081" /> : <AnimatedUserChip text={answers.amount} data-id="element-3082" />}
                  </AIMessage>
                </motion.div>}

              {/* Typing indicator before Q3 */}
              {step === 2 && typing && <motion.div key="typing-2" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3083">
                  <Typing data-id="element-3084" />
                </motion.div>}

              {/* Q3 */}
              {step >= 2 && !typing && <motion.div key="q3" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-3085">
                  <AIMessage pulse={activeIndex === 2 && !answers.litigation} data-id="element-3086">
                    <p className="font-medium text-primary mb-3" data-id="element-3087">
                      {QUESTIONS[2]}
                    </p>
                    {!answers.litigation ? <ChipGroup options={LITIGATION} onSelect={v => setAnswer('litigation', v)} data-id="element-3088" /> : <AnimatedUserChip text={answers.litigation} data-id="element-3089" />}
                  </AIMessage>
                </motion.div>}

              {/* Typing indicator before Q4 */}
              {step === 3 && typing && <motion.div key="typing-3" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3090">
                  <Typing data-id="element-3091" />
                </motion.div>}

              {/* Q4 */}
              {step >= 3 && !typing && <motion.div key="q4" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-3092">
                  <AIMessage pulse={activeIndex === 3 && !answers.outcome} data-id="element-3093">
                    <p className="font-medium text-primary mb-3" data-id="element-3094">
                      {QUESTIONS[3]}
                    </p>
                    {!answers.outcome ? <ChipGroup options={OUTCOMES} onSelect={v => setAnswer('outcome', v)} data-id="element-3095" /> : <AnimatedUserChip text={answers.outcome} data-id="element-3096" />}
                  </AIMessage>
                </motion.div>}

              {/* Typing indicator before completion */}
              {step === 4 && typing && <motion.div key="typing-final" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3097">
                  <Typing data-id="element-3098" />
                </motion.div>}

              {/* Completion */}
              {step >= 4 && !typing && <motion.div key="done" initial={{
              opacity: 0,
              y: 12,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-3099">
                  <AIMessage tone="success" data-id="element-3100">
                    <p className="font-medium text-primary" data-id="element-3101">
                      Thanks. Based on your answers, here are the support
                      options that may be most helpful.
                    </p>
                    <p className="text-sm text-secondary mt-1" data-id="element-3102">
                      Choose the path that works best for you below.
                    </p>
                  </AIMessage>
                </motion.div>}

              {/* Inline Recommendation */}
              {step >= 4 && !typing && recommendation && <motion.div key="recommendation" initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.45,
              ease: 'easeOut',
              delay: 0.1
            }} data-id="element-3103">
                  <AIMessage data-id="element-3104">
                    <div className="flex items-center gap-2 mb-3" data-id="element-3105">
                      <Sparkles className="w-4 h-4 text-violetBlue" data-id="element-3106" />
                      <p className="text-sm font-semibold text-primary" data-id="element-3107">
                        Recommended Next Steps
                      </p>
                    </div>
                    <RecommendationView rec={recommendation} selected={selectedPath} onSelect={handleRecommendationSelect} callBookingStep={callBookingStep} representationStep={representationStep} data-id="element-3108" />
                  </AIMessage>
                </motion.div>}

              {/* Booking - typing before window picker */}
              {callTyping && callBookingStep === 'idle' && <motion.div key="typing-booking-1" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3109">
                  <Typing data-id="element-3110" />
                </motion.div>}

              {/* Booking - window picker AI message */}
              {(callBookingStep === 'pickWindows' || callBookingStep === 'enterPhone' || callBookingStep === 'confirmed') && <motion.div key="booking-windows" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-3111">
                  <AIMessage data-id="element-3112">
                    <p className="font-medium text-primary mb-1" data-id="element-3113">
                      Pick two 4-hour windows when we can reach you.
                    </p>
                    <p className="text-xs text-secondary mb-3" data-id="element-3114">
                      Our team will try the first window. If we miss you, we'll
                      try the second.
                    </p>
                    {callBookingStep === 'pickWindows' ? <WindowPicker windows={callWindows} selected={selectedWindows} onToggle={toggleWindow} onContinue={confirmWindows} data-id="element-3115" /> : <div className="flex flex-col items-end gap-2" data-id="element-3116">
                        {selectedWindows.map(w => <AnimatedUserChip key={w} text={w} data-id="element-3117" />)}
                      </div>}
                  </AIMessage>
                </motion.div>}

              {/* Booking - typing before phone/note */}
              {callTyping && callBookingStep === 'pickWindows' && <motion.div key="typing-booking-2" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3118">
                  <Typing data-id="element-3119" />
                </motion.div>}

              {/* Booking - phone + note AI message */}
              {(callBookingStep === 'enterPhone' || callBookingStep === 'confirmed') && <motion.div key="booking-phone" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-3120">
                  <AIMessage data-id="element-3121">
                    <p className="font-medium text-primary mb-1" data-id="element-3122">
                      What number should we call, and anything we should know?
                    </p>
                    <p className="text-xs text-secondary mb-3" data-id="element-3123">
                      Your phone number stays private and is only used for this
                      call.
                    </p>
                    {callBookingStep === 'enterPhone' ? <PhoneNoteForm phone={callPhone} note={callNote} onPhoneChange={setCallPhone} onNoteChange={setCallNote} onConfirm={confirmBooking} data-id="element-3124" /> : <div className="flex flex-col items-end gap-2" data-id="element-3125">
                        <AnimatedUserChip text={callPhone} data-id="element-3126" />
                        {callNote.trim() && <AnimatedUserChip text={`Note: ${callNote.trim()}`} data-id="element-3127" />}
                      </div>}
                  </AIMessage>
                </motion.div>}

              {/* Booking - typing before success */}
              {callTyping && callBookingStep === 'enterPhone' && <motion.div key="typing-booking-3" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3128">
                  <Typing data-id="element-3129" />
                </motion.div>}

              {/* Booking - success message */}
              {callBookingStep === 'confirmed' && <motion.div key="booking-success" initial={{
              opacity: 0,
              y: 14,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.45,
              ease: 'easeOut'
            }} data-id="element-3130">
                  <AIMessage tone="success" data-id="element-3131">
                    <p className="font-medium text-primary" data-id="element-3132">
                      All set - your call is booked.
                    </p>
                    <p className="text-sm text-secondary mt-1.5 leading-relaxed" data-id="element-3133">
                      Our team will call you at{' '}
                      <span className="font-medium text-primary" data-id="element-3134">
                        {callPhone}
                      </span>{' '}
                      between{' '}
                      <span className="font-medium text-primary" data-id="element-3135">
                        {selectedWindows[0]}
                      </span>{' '}
                      or{' '}
                      <span className="font-medium text-primary" data-id="element-3136">
                        {selectedWindows[1]}
                      </span>
                      . You'll also receive a confirmation in your TELUS Digital
                      inbox.
                    </p>
                  </AIMessage>
                </motion.div>}

              {/* Representation - typing before window picker */}
              {repTyping && representationStep === 'idle' && <motion.div key="typing-rep-1" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3137">
                  <Typing data-id="element-3138" />
                </motion.div>}

              {/* Representation - window picker AI message */}
              {(representationStep === 'pickWindows' || representationStep === 'enterPostalCode' || representationStep === 'confirmed') && <motion.div key="rep-windows" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-3139">
                  <AIMessage data-id="element-3140">
                    <p className="font-medium text-primary mb-1" data-id="element-3141">
                      Pick two 4-hour windows when a lawyer can meet with you.
                    </p>
                    <p className="text-xs text-secondary mb-3" data-id="element-3142">
                      Earliest available is 48 hours from now so we have time to
                      match someone in your area.
                    </p>
                    {representationStep === 'pickWindows' ? <WindowPicker windows={repWindows} selected={representationWindows} onToggle={toggleRepWindow} onContinue={confirmRepWindows} data-id="element-3143" /> : <div className="flex flex-col items-end gap-2" data-id="element-3144">
                        {representationWindows.map(w => <AnimatedUserChip key={w} text={w} data-id="element-3145" />)}
                      </div>}
                  </AIMessage>
                </motion.div>}

              {/* Representation - typing before postal code */}
              {repTyping && representationStep === 'pickWindows' && <motion.div key="typing-rep-2" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3146">
                  <Typing data-id="element-3147" />
                </motion.div>}

              {/* Representation - postal code AI message */}
              {(representationStep === 'enterPostalCode' || representationStep === 'confirmed') && <motion.div key="rep-postal" initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-3148">
                  <AIMessage data-id="element-3149">
                    <p className="font-medium text-primary mb-1" data-id="element-3150">
                      What's your postal code?
                    </p>
                    <p className="text-xs text-secondary mb-3" data-id="element-3151">
                      We'll use it to find a lawyer in your area.
                    </p>
                    {representationStep === 'enterPostalCode' ? <PostalCodeForm value={postalCode} onChange={setPostalCode} onConfirm={confirmRepresentation} data-id="element-3152" /> : <div className="flex flex-col items-end gap-2" data-id="element-3153">
                        <AnimatedUserChip text={postalCode} data-id="element-3154" />
                      </div>}
                  </AIMessage>
                </motion.div>}

              {/* Representation - typing before recap */}
              {repTyping && representationStep === 'enterPostalCode' && <motion.div key="typing-rep-3" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} data-id="element-3155">
                  <Typing data-id="element-3156" />
                </motion.div>}

              {/* Representation - success + recap */}
              {representationStep === 'confirmed' && <motion.div key="rep-success" initial={{
              opacity: 0,
              y: 14,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.45,
              ease: 'easeOut'
            }} data-id="element-3157">
                  <AIMessage tone="success" data-id="element-3158">
                    <p className="font-medium text-primary" data-id="element-3159">
                      We're matching you with a lawyer.
                    </p>
                    <p className="text-sm text-secondary mt-1 mb-3" data-id="element-3160">
                      Here's a summary of your request.
                    </p>
                    <RepresentationRecapCard answers={answers} matterNote={matterNote} windows={representationWindows} postalCode={postalCode} legalArea={getLegalArea(answers.matter)} data-id="element-3161" />
                  </AIMessage>
                </motion.div>}
            </AnimatePresence>

            <div ref={bottomRef} data-id="element-3162" />
          </div>

          {/* Persistent input row */}
          <div className="border-t border-borderColor px-5 py-4 bg-white" data-id="element-3163">
            <div className="flex items-center gap-2 bg-aliceBlue border border-borderColor rounded-lg px-3 py-2" data-id="element-3164">
              <Paperclip className="w-4 h-4 text-disabled flex-shrink-0" data-id="element-3165" />
              <input disabled placeholder="Type a message or use the answer chips above..." className="flex-1 min-w-0 bg-transparent text-sm text-primary placeholder-disabled focus:outline-none" data-id="element-3166" />
              <button disabled type="button" className="p-1.5 rounded-md bg-disabled text-white cursor-not-allowed flex-shrink-0" aria-label="Send" data-id="element-3167">
                <Send className="w-4 h-4" data-id="element-3168" />
              </button>
            </div>
            {!presentationMode && <div className="flex items-center justify-center gap-1.5 mt-2.5 text-[11px] text-secondary" data-id="element-3169">
                <ShieldCheck className="w-3.5 h-3.5 text-violetBlue flex-shrink-0" data-id="element-3170" />
                <p data-id="element-3171">
                  This triage helps guide you to the right LawVo service - it is
                  not legal advice.
                </p>
              </div>}
          </div>
        </section>
      </main>
    </div>;
}
/* --- Sub-components --- */
function ProgressDots({
  step,
  total
}: {
  step: number;
  total: number;
}) {
  const safe = Math.min(step, total);
  return <div className="flex items-center gap-2" data-id="element-3172">
      <div className="flex items-center gap-1" data-id="element-3173">
        {Array.from({
        length: total
      }).map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < safe ? 'bg-violetBlue' : 'bg-disabled'}`} data-id="element-3174" />)}
      </div>
    </div>;
}
function AIMessage({
  children,
  tone = 'default',
  pulse = false
}: {
  children: React.ReactNode;
  tone?: 'default' | 'success';
  pulse?: boolean;
}) {
  return <div className="flex items-start gap-3" data-id="element-3175">
      <motion.div className={`relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${tone === 'success' ? 'bg-turquoise/15' : 'bg-aliceBlue'}`} animate={pulse ? {
      boxShadow: ['0 0 0 0 rgba(120,84,255,0.35)', '0 0 0 8px rgba(120,84,255,0)', '0 0 0 0 rgba(120,84,255,0)']
    } : {
      boxShadow: '0 0 0 0 rgba(120,84,255,0)'
    }} transition={pulse ? {
      duration: 1.6,
      repeat: Infinity,
      ease: 'easeOut'
    } : {
      duration: 0.3
    }} data-id="element-3176">
        {tone === 'success' ? <CheckCircle className="w-4 h-4 text-turquoise" data-id="element-3177" /> : <Sparkles className="w-4 h-4 text-violetBlue" data-id="element-3178" />}
      </motion.div>
      <div className={`flex-1 bg-white border border-borderColor rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm text-sm text-primary leading-relaxed ${tone === 'success' ? 'border-turquoise/30' : ''}`} data-id="element-3179">
        {children}
      </div>
    </div>;
}
function ChipGroup({
  options,
  onSelect
}: {
  options: string[];
  onSelect: (v: string) => void;
}) {
  return <div className="flex flex-wrap gap-2" data-id="element-3180">
      {options.map((o, i) => <motion.button key={o} type="button" onClick={() => onSelect(o)} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      delay: 0.04 * i
    }} whileHover={{
      y: -2
    }} whileTap={{
      scale: 0.94
    }} className="px-3 py-1.5 bg-white border border-borderColor hover:border-violetBlue hover:bg-aliceBlue hover:shadow-sm text-primary text-sm rounded-full transition-colors" data-id="element-3181">
          {o}
        </motion.button>)}
    </div>;
}
function AnimatedUserChip({
  text
}: {
  text: string;
}) {
  return <div className="flex justify-end" data-id="element-3182">
      <motion.span initial={{
      opacity: 0,
      scale: 0.85,
      x: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      x: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 22
    }} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violetBlue text-white text-sm font-medium rounded-full shadow-sm" data-id="element-3183">
        <CheckCircle className="w-3.5 h-3.5" data-id="element-3184" />
        {text}
      </motion.span>
    </div>;
}
function UserChip({
  text
}: {
  text: string;
}) {
  return <div className="flex justify-end" data-id="element-3185">
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violetBlue text-white text-sm font-medium rounded-full" data-id="element-3186">
        <CheckCircle className="w-3.5 h-3.5" data-id="element-3187" />
        {text}
      </span>
    </div>;
}
function SummaryRow({
  label,
  value,
  muted
}: {
  label: string;
  value?: string;
  muted?: boolean;
}) {
  return <div className="flex items-start justify-between gap-3 border-b border-borderColor last:border-b-0 pb-2 last:pb-0" data-id="element-3188">
      <dt className="text-xs uppercase tracking-wide text-secondary font-semibold w-32 flex-shrink-0" data-id="element-3189">
        {label}
      </dt>
      <dd className={`text-sm text-right flex-1 ${value ? muted ? 'text-secondary' : 'text-primary font-medium' : 'text-disabled italic'}`} data-id="element-3190">
        {value || 'Pending'}
      </dd>
    </div>;
}
function RecommendationView({
  rec,
  selected,
  onSelect,
  callBookingStep = 'idle',
  representationStep = 'idle'
}: {
  rec: {
    primary: ServiceKey;
    also: ServiceKey[];
    available: ServiceKey[];
    explanation: string;
  };
  selected: ServiceKey | null;
  onSelect: (k: ServiceKey) => void;
  callBookingStep?: 'idle' | 'pickWindows' | 'enterPhone' | 'confirmed';
  representationStep?: 'idle' | 'pickWindows' | 'enterPostalCode' | 'confirmed';
}) {
  const Primary = SERVICES[rec.primary];
  const PrimaryIcon = Primary.icon;
  const isPhonePrimary = Primary.key === 'phone';
  const isRepPrimary = Primary.key === 'representation';
  const bookingInProgress = isPhonePrimary && (callBookingStep === 'pickWindows' || callBookingStep === 'enterPhone');
  const bookingConfirmed = isPhonePrimary && callBookingStep === 'confirmed';
  const repInProgress = isRepPrimary && (representationStep === 'pickWindows' || representationStep === 'enterPostalCode');
  const repConfirmed = isRepPrimary && representationStep === 'confirmed';
  return <div className="space-y-4" data-id="element-3191">
      {/* Primary */}
      <div className="border-2 border-violetBlue rounded-xl p-4 bg-gradient-to-br from-violetBlue/5 to-white" data-id="element-3192">
        <div className="flex items-center gap-2 mb-2" data-id="element-3193">
          <span className="px-2 py-0.5 bg-violetBlue text-white text-[10px] font-bold rounded-full uppercase tracking-wide" data-id="element-3194">
            Recommended
          </span>
        </div>
        <div className="flex items-start gap-3" data-id="element-3195">
          <div className="p-2 bg-violetBlue/10 rounded-lg flex-shrink-0" data-id="element-3196">
            <PrimaryIcon className="w-5 h-5 text-violetBlue" data-id="element-3197" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-3198">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-3199">
              <h4 className="text-base font-semibold text-primary" data-id="element-3200">
                {Primary.title}
              </h4>
              <PricingBadge pricing={Primary.pricing} data-id="element-3201" />
            </div>
            <p className="text-xs text-secondary leading-relaxed mt-0.5" data-id="element-3202">
              {Primary.description}
            </p>
            <button type="button" disabled={bookingInProgress || bookingConfirmed || repInProgress || repConfirmed} onClick={() => onSelect(Primary.key as ServiceKey)} className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium py-2 px-3 rounded-lg transition-colors ${bookingConfirmed || repConfirmed ? 'bg-turquoise text-white' : bookingInProgress || repInProgress ? 'bg-aliceBlue border border-borderColor text-secondary cursor-not-allowed' : selected === Primary.key && !isPhonePrimary && !isRepPrimary ? 'bg-turquoise text-white' : 'bg-violetBlue hover:bg-[#6844E8] text-white'}`} data-id="element-3203">
              {bookingConfirmed ? <>
                  <CheckCircle className="w-4 h-4" data-id="element-3204" />
                  Call Booked
                </> : repConfirmed ? <>
                  <CheckCircle className="w-4 h-4" data-id="element-3205" />
                  Lawyer Requested
                </> : bookingInProgress ? <>
                  <Sparkles className="w-4 h-4 animate-pulse" data-id="element-3206" />
                  Booking in progress...
                </> : repInProgress ? <>
                  <Sparkles className="w-4 h-4 animate-pulse" data-id="element-3207" />
                  Matching in progress...
                </> : selected === Primary.key && !isPhonePrimary && !isRepPrimary ? <>
                  <CheckCircle className="w-4 h-4" data-id="element-3208" />
                  Selected
                </> : <>
                  {Primary.cta}
                  <ArrowRight className="w-4 h-4" data-id="element-3209" />
                </>}
            </button>
          </div>
        </div>

        {/* Why */}
        <div className="mt-3 bg-white border border-borderColor rounded-lg p-3" data-id="element-3210">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-3211">
            <Sparkles className="w-3.5 h-3.5 text-violetBlue" data-id="element-3212" />
            <span className="text-xs font-semibold text-primary" data-id="element-3213">
              Why we recommend this
            </span>
          </div>
          <p className="text-xs text-secondary leading-relaxed" data-id="element-3214">
            {rec.explanation}
          </p>
        </div>
      </div>

      {/* Also available */}
      {rec.also.length > 0 && <div data-id="element-3215">
          <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2" data-id="element-3216">
            Also Available
          </div>
          <div className="space-y-2" data-id="element-3217">
            {rec.also.map(k => <ServiceRow key={k} serviceKey={k} selected={selected === k} onSelect={() => onSelect(k)} data-id="element-3218" />)}
          </div>
        </div>}

      {/* Not suggested */}
      {rec.available.length > 0 && <div data-id="element-3219">
          <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2" data-id="element-3220">
            Not Suggested for This Matter
          </div>
          <div className="space-y-1" data-id="element-3221">
            {rec.available.map(k => <CompactServiceRow key={k} serviceKey={k} selected={selected === k} onSelect={() => onSelect(k)} data-id="element-3222" />)}
          </div>
        </div>}

      <div className="flex items-start gap-2 bg-aliceBlue border border-borderColor rounded-lg p-3" data-id="element-3223">
        <AlertCircle className="w-4 h-4 text-violetBlue mt-0.5 flex-shrink-0" data-id="element-3224" />
        <p className="text-xs text-secondary leading-relaxed" data-id="element-3225">
          This recommendation is based on your answers and is not legal advice.
          You can still choose another support option.
        </p>
      </div>
    </div>;
}
function CompactServiceRow({
  serviceKey,
  selected,
  onSelect
}: {
  serviceKey: ServiceKey;
  selected: boolean;
  onSelect: () => void;
}) {
  const s = SERVICES[serviceKey];
  const Icon = s.icon;
  return <button type="button" onClick={onSelect} className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border transition-colors text-left ${selected ? 'border-turquoise bg-turquoise/5' : 'border-borderColor bg-aliceBlue/40 hover:bg-white hover:border-violetBlue'}`} data-id="element-3226">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${selected ? 'text-turquoise' : 'text-secondary'}`} data-id="element-3227" />
      <span className="text-xs font-medium text-primary truncate flex-1 min-w-0" data-id="element-3228">
        {s.title}
      </span>
      <PricingBadge pricing={s.pricing} data-id="element-3229" />
      {selected ? <CheckCircle className="w-3.5 h-3.5 text-turquoise flex-shrink-0" data-id="element-3230" /> : <ChevronRight className="w-3.5 h-3.5 text-disabled flex-shrink-0" data-id="element-3231" />}
    </button>;
}
function ServiceRow({
  serviceKey,
  selected,
  onSelect,
  dimmed
}: {
  serviceKey: ServiceKey;
  selected: boolean;
  onSelect: () => void;
  dimmed?: boolean;
}) {
  const s = SERVICES[serviceKey];
  const Icon = s.icon;
  return <div className={`border border-borderColor rounded-lg p-3 flex items-start gap-3 transition-colors ${dimmed ? 'bg-aliceBlue/40 opacity-80' : 'bg-white hover:border-violetBlue'}`} data-id="element-3232">
      <div className={`p-1.5 rounded-lg flex-shrink-0 ${dimmed ? 'bg-white' : 'bg-aliceBlue'}`} data-id="element-3233">
        <Icon className={`w-4 h-4 ${dimmed ? 'text-secondary' : 'text-violetBlue'}`} data-id="element-3234" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-3235">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-3236">
          <div className="text-sm font-semibold text-primary" data-id="element-3237">{s.title}</div>
          <PricingBadge pricing={s.pricing} data-id="element-3238" />
        </div>
        <div className="text-xs text-secondary leading-relaxed mt-0.5" data-id="element-3239">
          {s.description}
        </div>
        <button type="button" onClick={onSelect} className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold transition-colors ${selected ? 'text-turquoise' : 'text-violetBlue hover:text-[#6844E8]'}`} data-id="element-3240">
          {selected ? <>
              <CheckCircle className="w-3.5 h-3.5" data-id="element-3241" /> Selected
            </> : <>
              {s.cta}
              <ChevronRight className="w-3.5 h-3.5" data-id="element-3242" />
            </>}
        </button>
      </div>
    </div>;
}
function PricingBadge({
  pricing
}: {
  pricing: 'included' | 'addon';
}) {
  if (pricing === 'included') {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-turquoise/15 text-turquoise text-[10px] font-semibold rounded-full uppercase tracking-wide" data-id="element-3243">
        <CheckCircle className="w-2.5 h-2.5" data-id="element-3244" />
        Included in your plan
      </span>;
  }
  return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-aliceBlue text-violetBlue text-[10px] font-semibold rounded-full uppercase tracking-wide border border-violetBlue/20" data-id="element-3245">
      Additional fee
    </span>;
}
// Call booking helpers ------------------------------------------------------
const POSTAL_CODE_RE = /^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/;
function isValidPostalCode(code: string) {
  return POSTAL_CODE_RE.test(code.trim());
}
function formatPostalCode(raw: string) {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
}
const LEGAL_AREA_MAP: Record<string, string> = {
  Litigation: 'Litigation',
  'Residential Landlord & Tenant': 'Residential Landlord & Tenant Law',
  'Real Estate Transaction': 'Real Estate Transaction Law',
  'Real Estate Litigation': 'Real Estate Litigation',
  'Contract Dispute': 'Contract Law',
  'Debt / Collections': 'Debt & Collections Law',
  Family: 'Family Law',
  'Consumer Issue': 'Consumer Protection Law'
};
function getLegalArea(matter?: string) {
  if (!matter) return 'General Practice';
  return LEGAL_AREA_MAP[matter] ?? 'General Practice';
}
type CallWindowSlot = {
  id: string;
  dayLabel: string;
  timeLabel: string;
  startsAt: Date;
};
function getCallWindows(minHoursOffset = 8): CallWindowSlot[] {
  const now = new Date();
  const earliest = new Date(now.getTime() + minHoursOffset * 60 * 60 * 1000);
  const TIME_BLOCKS = [{
    start: 8,
    label: '8:00 AM - 12:00 PM'
  }, {
    start: 12,
    label: '12:00 PM - 4:00 PM'
  }, {
    start: 16,
    label: '4:00 PM - 8:00 PM'
  }];
  const fmt = new Intl.DateTimeFormat('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  const slots: CallWindowSlot[] = [];
  const dayCursor = new Date(now);
  dayCursor.setHours(0, 0, 0, 0);
  dayCursor.setDate(dayCursor.getDate() + 1);
  let safety = 0;
  while (slots.length < 6 && safety < 14) {
    const dow = dayCursor.getDay(); // 0 = Sun, 6 = Sat
    if (dow === 0 || dow === 6) {
      dayCursor.setDate(dayCursor.getDate() + 1);
      safety++;
      continue;
    }
    const dayLabel = fmt.format(dayCursor);
    for (const block of TIME_BLOCKS) {
      const start = new Date(dayCursor);
      start.setHours(block.start, 0, 0, 0);
      if (start.getTime() >= earliest.getTime()) {
        slots.push({
          id: `${dayCursor.toISOString().slice(0, 10)}-${block.start}`,
          dayLabel,
          timeLabel: block.label,
          startsAt: start
        });
        if (slots.length >= 6) break;
      }
    }
    dayCursor.setDate(dayCursor.getDate() + 1);
    safety++;
  }
  return slots;
}
function formatWindowLabel(slot: CallWindowSlot) {
  return `${slot.dayLabel} - ${slot.timeLabel}`;
}
function WindowPicker({
  windows,
  selected,
  onToggle,
  onContinue
}: {
  windows: CallWindowSlot[];
  selected: string[];
  onToggle: (label: string) => void;
  onContinue: () => void;
}) {
  // Group by dayLabel preserving insertion order
  const grouped: {
    dayLabel: string;
    slots: CallWindowSlot[];
  }[] = [];
  for (const slot of windows) {
    const last = grouped[grouped.length - 1];
    if (last && last.dayLabel === slot.dayLabel) {
      last.slots.push(slot);
    } else {
      grouped.push({
        dayLabel: slot.dayLabel,
        slots: [slot]
      });
    }
  }
  const canContinue = selected.length === 2;
  return <div className="space-y-4" data-id="element-3246">
      {grouped.map(g => <div key={g.dayLabel} data-id="element-3247">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-secondary mb-2" data-id="element-3248">
            {g.dayLabel}
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3249">
            {g.slots.map((slot, i) => {
          const label = formatWindowLabel(slot);
          const isSelected = selected.includes(label);
          const atLimit = !isSelected && selected.length >= 2;
          return <motion.button key={slot.id} type="button" disabled={atLimit} onClick={() => onToggle(label)} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.25,
            delay: 0.04 * i
          }} whileHover={!atLimit ? {
            y: -2
          } : undefined} whileTap={!atLimit ? {
            scale: 0.95
          } : undefined} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${isSelected ? 'bg-violetBlue text-white shadow-sm' : atLimit ? 'bg-aliceBlue border border-borderColor text-disabled cursor-not-allowed' : 'bg-white border border-borderColor text-primary hover:border-violetBlue hover:bg-aliceBlue hover:shadow-sm'}`} data-id="element-3250">
                  {isSelected && <CheckCircle className="w-3.5 h-3.5" data-id="element-3251" />}
                  {slot.timeLabel}
                </motion.button>;
        })}
          </div>
        </div>)}
      <div className="flex items-center justify-between gap-3 pt-1" data-id="element-3252">
        <span className="text-xs text-secondary" data-id="element-3253">
          {selected.length} of 2 selected
        </span>
        <motion.button type="button" disabled={!canContinue} onClick={onContinue} whileHover={canContinue ? {
        y: -1
      } : undefined} whileTap={canContinue ? {
        scale: 0.97
      } : undefined} className={`inline-flex items-center gap-1.5 text-sm font-medium py-2 px-3.5 rounded-lg transition-colors ${canContinue ? 'bg-violetBlue hover:bg-[#6844E8] text-white' : 'bg-aliceBlue border border-borderColor text-disabled cursor-not-allowed'}`} data-id="element-3254">
          Continue
          <ArrowRight className="w-4 h-4" data-id="element-3255" />
        </motion.button>
      </div>
    </div>;
}
function PhoneNoteForm({
  phone,
  note,
  onPhoneChange,
  onNoteChange,
  onConfirm
}: {
  phone: string;
  note: string;
  onPhoneChange: (v: string) => void;
  onNoteChange: (v: string) => void;
  onConfirm: () => void;
}) {
  const digits = phone.replace(/\D/g, '');
  const isValid = digits.length >= 10;
  return <div className="space-y-3" data-id="element-3256">
      <div data-id="element-3257">
        <label className="block text-xs font-semibold uppercase tracking-wide text-secondary mb-1.5" data-id="element-3258">
          Phone number
        </label>
        <div className="flex items-center gap-2 bg-white border border-borderColor rounded-lg px-3 py-2 focus-within:border-violetBlue focus-within:ring-2 focus-within:ring-violetBlue/20 transition-all" data-id="element-3259">
          <Phone className="w-4 h-4 text-violetBlue flex-shrink-0" data-id="element-3260" />
          <input type="tel" inputMode="tel" value={phone} onChange={e => onPhoneChange(e.target.value.replace(/[^\d+\s()-]/g, ''))} placeholder="(555) 123-4567" className="flex-1 bg-transparent text-sm text-primary placeholder-disabled focus:outline-none" data-id="element-3261" />
        </div>
      </div>
      <div data-id="element-3262">
        <label className="block text-xs font-semibold uppercase tracking-wide text-secondary mb-1.5" data-id="element-3263">
          Anything our team should know?{' '}
          <span className="font-normal normal-case text-disabled" data-id="element-3264">
            (Optional)
          </span>
        </label>
        <textarea rows={2} value={note} onChange={e => onNoteChange(e.target.value)} placeholder="e.g. Best to reach me on French; I'd prefer a callback before 6pm." className="w-full bg-white border border-borderColor rounded-lg px-3 py-2 text-sm text-primary placeholder-disabled focus:outline-none focus:border-violetBlue focus:ring-2 focus:ring-violetBlue/20 resize-none transition-all" data-id="element-3265" />
      </div>
      <div className="flex justify-end pt-1" data-id="element-3266">
        <motion.button type="button" disabled={!isValid} onClick={onConfirm} whileHover={isValid ? {
        y: -1
      } : undefined} whileTap={isValid ? {
        scale: 0.97
      } : undefined} className={`inline-flex items-center gap-1.5 text-sm font-medium py-2 px-3.5 rounded-lg transition-colors ${isValid ? 'bg-violetBlue hover:bg-[#6844E8] text-white' : 'bg-aliceBlue border border-borderColor text-disabled cursor-not-allowed'}`} data-id="element-3267">
          <CheckCircle className="w-4 h-4" data-id="element-3268" />
          Confirm request
        </motion.button>
      </div>
    </div>;
}
function PostalCodeForm({
  value,
  onChange,
  onConfirm
}: {
  value: string;
  onChange: (v: string) => void;
  onConfirm: () => void;
}) {
  const valid = isValidPostalCode(value);
  const touched = value.replace(/\s/g, '').length >= 3;
  const showError = touched && !valid;
  return <div className="space-y-3" data-id="element-3269">
      <div data-id="element-3270">
        <label className="block text-xs font-semibold uppercase tracking-wide text-secondary mb-1.5" data-id="element-3271">
          Postal code
        </label>
        <div className={`flex items-center gap-2 bg-white border rounded-lg px-3 py-2 transition-all focus-within:ring-2 ${showError ? 'border-destructive focus-within:border-destructive focus-within:ring-destructive/20' : 'border-borderColor focus-within:border-violetBlue focus-within:ring-violetBlue/20'}`} data-id="element-3272">
          <MapPin className="w-4 h-4 text-violetBlue flex-shrink-0" data-id="element-3273" />
          <input type="text" inputMode="text" autoCapitalize="characters" value={value} onChange={e => onChange(formatPostalCode(e.target.value))} placeholder="e.g. M5V 2H1" maxLength={7} className="flex-1 bg-transparent text-sm text-primary placeholder-disabled focus:outline-none tracking-wide uppercase" data-id="element-3274" />
        </div>
        {showError && <p className="mt-1.5 text-xs text-destructive" data-id="element-3275">
            Enter a valid Canadian postal code (e.g. M5V 2H1).
          </p>}
      </div>
      <div className="flex justify-end pt-1" data-id="element-3276">
        <motion.button type="button" disabled={!valid} onClick={onConfirm} whileHover={valid ? {
        y: -1
      } : undefined} whileTap={valid ? {
        scale: 0.97
      } : undefined} className={`inline-flex items-center gap-1.5 text-sm font-medium py-2 px-3.5 rounded-lg transition-colors ${valid ? 'bg-violetBlue hover:bg-[#6844E8] text-white' : 'bg-aliceBlue border border-borderColor text-disabled cursor-not-allowed'}`} data-id="element-3277">
          <Scale className="w-4 h-4" data-id="element-3278" />
          Find my lawyer
        </motion.button>
      </div>
    </div>;
}
function RepresentationRecapCard({
  answers,
  matterNote,
  windows,
  postalCode,
  legalArea
}: {
  answers: Answers;
  matterNote: string;
  windows: string[];
  postalCode: string;
  legalArea: string;
}) {
  const rows: {
    label: string;
    value?: string;
  }[] = [{
    label: 'Matter type',
    value: answers.matter
  }, {
    label: 'Dispute amount',
    value: answers.amount
  }, {
    label: 'Litigation status',
    value: answers.litigation
  }, {
    label: 'Desired outcome',
    value: answers.outcome
  }];
  return <div className="space-y-4" data-id="element-3279">
      {/* Legal area + postal code header */}
      <div className="bg-white border border-borderColor rounded-xl p-4 space-y-3" data-id="element-3280">
        <div data-id="element-3281">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-secondary mb-1.5" data-id="element-3282">
            Legal area
          </p>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-turquoise/15 text-[#0d8a82] text-xs font-semibold rounded-full" data-id="element-3283">
            <Scale className="w-3.5 h-3.5" data-id="element-3284" />
            {legalArea}
          </span>
        </div>
        <div data-id="element-3285">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-secondary mb-1.5" data-id="element-3286">
            Where
          </p>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary" data-id="element-3287">
            <MapPin className="w-4 h-4 text-violetBlue" data-id="element-3288" />
            {postalCode}
          </span>
        </div>
      </div>

      {/* When */}
      <div className="bg-white border border-borderColor rounded-xl p-4" data-id="element-3289">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-secondary mb-2" data-id="element-3290">
          When
        </p>
        <div className="space-y-1.5" data-id="element-3291">
          {windows.map(w => <div key={w} className="flex items-start gap-2 text-sm text-primary" data-id="element-3292">
              <CheckCircle className="w-4 h-4 text-violetBlue mt-0.5 flex-shrink-0" data-id="element-3293" />
              <span data-id="element-3294">{w}</span>
            </div>)}
        </div>
      </div>

      {/* About your matter */}
      <div className="bg-white border border-borderColor rounded-xl p-4" data-id="element-3295">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-secondary mb-3" data-id="element-3296">
          About your matter
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5" data-id="element-3297">
          {rows.map(r => <div key={r.label} className="min-w-0" data-id="element-3298">
              <dt className="text-[10px] uppercase tracking-wider text-secondary font-semibold" data-id="element-3299">
                {r.label}
              </dt>
              <dd className="text-sm text-primary font-medium break-words" data-id="element-3300">
                {r.value || '-'}
              </dd>
            </div>)}
        </dl>
        {matterNote && <div className="mt-3 pt-3 border-t border-borderColor" data-id="element-3301">
            <dt className="text-[10px] uppercase tracking-wider text-secondary font-semibold mb-1" data-id="element-3302">
              Description
            </dt>
            <dd className="text-xs text-secondary leading-relaxed italic" data-id="element-3303">
              "{matterNote}"
            </dd>
          </div>}
      </div>

      {/* Footer */}
      <p className="text-xs text-secondary text-center pt-1" data-id="element-3304">
        You'll receive an email from your matched lawyer within one business
        day.
      </p>
    </div>;
}
// New: animated typing indicator
function Typing() {
  return <div className="flex items-start gap-3" data-id="element-3305">
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-aliceBlue" data-id="element-3306">
        <Sparkles className="w-4 h-4 text-violetBlue" data-id="element-3307" />
      </div>
      <div className="bg-white border border-borderColor rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1.5" data-id="element-3308">
        {[0, 1, 2].map(i => <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-violetBlue" animate={{
        y: [0, -3, 0],
        opacity: [0.4, 1, 0.4]
      }} transition={{
        duration: 0.9,
        repeat: Infinity,
        delay: i * 0.15,
        ease: 'easeInOut'
      }} data-id="element-3309" />)}
      </div>
    </div>;
}